import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  IDocumentRequestPayload,
  IDocumentsSummary,
  IEmployeeDocument,
  IEmployeeDocumentDetails,
  IEmployeeDocumentsSummary,
} from "@/types/(company)/documents";
import type {
  IDocument,
  IDocumentRequest,
  IDocumentRequestStats,
  IDocumentVersion,
} from "@/types/document";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyDocumentsApi = createApi({
  reducerPath: "companyDocumentsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "companyGetDocuments",
    "companyGetSentDocumentsRequests",
    "companyGetRecievedDocumentsRequests",
    "companyGetDocumentsSummary",
    "companyGetEmployeeDocumentsSummary",
    "DocumentById",
    "employeeDocuments",
    "DocumentRequestById",
    "companyDocRequestStats",
    "DocumentVersions",
  ],
  endpoints: (builder) => ({
    companyGetDocumentsSummary: builder.query<
      IApiResponse<IDocumentsSummary>,
      void
    >({
      query: () => ({
        url: "/company/documents/counts",
      }),
      providesTags: ["companyGetDocumentsSummary"],
    }),
    companyGetEmployeeDocuments: builder.query<
      IApiResponse<IEmployeeDocumentDetails[]>,
      any
    >({
      query: (params) => ({
        url: "/company/documents/employee-docs-details",
        params,
      }),
      providesTags: ["employeeDocuments"],
    }),
    companyGetEmployeeDocumentsSummary: builder.query<
      IApiResponse<IEmployeeDocumentsSummary>,
      string
    >({
      query: (id) => ({
        url: `/company/documents/employee-counts/${id}`,
      }),
      providesTags: (_result, _error, id) => [
        { type: "companyGetEmployeeDocumentsSummary", id },
      ],
    }),
    companyGetEmployeeDocumentsById: builder.query<
      IApiResponse<IEmployeeDocument>,
      { id: string; params?: any }
    >({
      query: ({ id, params }) => ({
        url: `/company/documents/employee-docs/${id}`,
        params,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "employeeDocuments", id },
      ],
    }),
    companyGetPaginatedDocuments: builder.query<IApiResponse<IDocument[]>, any>(
      {
        query: (params) => ({
          url: "/company/documents/company-docs",
          params,
        }),
        providesTags: ["companyGetDocuments"],
      },
    ),
    companyGetDocuments: builder.infiniteQuery<
      IApiResponse<IDocument[]>,
      any,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (resp) => {
          const { page, totalPages } = resp.meta;
          return page < totalPages ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: "/company/documents/company-docs",
        method: "GET",
        params: {
          ...queryArg,
          page: pageParam,
        },
      }),
      providesTags: ["companyGetDocuments"],
    }),
    companyGetDocumentById: builder.query<IApiResponse<IDocument>, string>({
      query: (id) => ({
        url: `/company/documents/by-id/${id}`,
      }),
      providesTags: (_result, _error, id) => [{ type: "DocumentById", id }],
    }),
    companyGetDocumentVersions: builder.query<
      IApiResponse<IDocumentVersion[]>,
      string | number
    >({
      query: (id) => ({
        url: `/company/documents/${id}/versions`,
      }),
      // Stringified on both sides: callers pass a route param (string) or a
      // row id (number), and tag ids compare strictly.
      providesTags: (_result, _error, id) => [
        { type: "DocumentVersions", id: String(id) },
      ],
    }),
    companyAddDocument: builder.mutation<IApiResponse<void>, FormData>({
      query: (data) => ({
        url: "/company/documents",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, formData) => [
        "companyGetDocuments",
        "employeeDocuments",
        { type: "DocumentById", id: formData.get("userId") as string },
        {
          type: "companyGetEmployeeDocumentsSummary",
          id: formData.get("userId") as string,
        },
      ],
    }),
    companyUpdateDocument: builder.mutation<
      IApiResponse<void>,
      { id: string | number; data: FormData }
    >({
      query: ({ id, data }) => ({
        url: `/company/documents/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "companyGetDocuments",
        "employeeDocuments",
        { type: "DocumentById", id },
        { type: "DocumentVersions", id: String(id) },
      ],
    }),
    companyDeleteDocumentById: builder.mutation<IApiResponse<void>, number>({
      query: (id) => ({
        url: `/company/documents/${id}`,
        method: "DELETE",
      }),
      // Computed, not a static array: RTK Query skips a static
      // `invalidatesTags` when the mutation rejects, so a 404 — the row was
      // already deleted (another tab, a double submit) — left the deleted row
      // on screen, and every retry 404'd again until a manual refresh.
      // Refetching on 404 clears it.
      invalidatesTags: (_result, error) =>
        !error ||
        (typeof error === "object" && "status" in error && error.status === 404)
          ? ["employeeDocuments", "companyGetDocuments"]
          : [],
    }),
    companyGetSentDocumentsRequests: builder.query<
      IApiResponse<IDocumentRequest[]>,
      any
    >({
      query: (params) => ({
        url: "/company/document-requests/sent-requests",
        params,
      }),
      providesTags: ["companyGetSentDocumentsRequests"],
    }),
    companyGetRecievedDocumentsRequests: builder.query<
      IApiResponse<IDocumentRequest[]>,
      any
    >({
      query: (params) => ({
        url: "/company/document-requests/received-requests",
        params,
      }),
      providesTags: ["companyGetRecievedDocumentsRequests"],
    }),
    getCompanyDocumentRequestStats: builder.query<
      IApiResponse<IDocumentRequestStats>,
      { type: "sent" | "received" }
    >({
      query: ({ type }) => ({
        url: "/company/document-requests/requests-stats",
        method: "GET",
        params: { type },
      }),
      providesTags: ["companyDocRequestStats"],
    }),
    companyCancelSentDocumentRequest: builder.mutation<IApiResponse<void>, any>(
      {
        query: (id) => ({
          url: `/company/document-requests/${id}/cancel`,
          method: "PUT",
        }),
        invalidatesTags: (_result, _error, id) => [
          "companyGetSentDocumentsRequests",
          "companyDocRequestStats",
          { type: "DocumentRequestById", id },
        ],
      },
    ),
    companyReopenSentDocumentRequest: builder.mutation<IApiResponse<void>, any>(
      {
        query: (id) => ({
          url: `/company/document-requests/${id}/reopen`,
          method: "PUT",
        }),
        invalidatesTags: (_result, _error, id) => [
          "companyGetSentDocumentsRequests",
          "companyDocRequestStats",
          { type: "DocumentRequestById", id },
        ],
      },
    ),
    createDocumentRequest: builder.mutation<
      IApiResponse<IDocumentRequest>,
      IDocumentRequestPayload
    >({
      query: (data) => ({
        url: "/company/document-requests",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        "companyGetSentDocumentsRequests",
        "companyGetDocumentsSummary",
        "companyDocRequestStats",
      ],
    }),
    companyUpdateDocumentRequest: builder.mutation<
      IApiResponse<void>,
      { id: string | number; data: IDocumentRequestPayload }
    >({
      query: ({ id, data }) => ({
        url: `/company/document-requests/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "companyGetSentDocumentsRequests",
        { type: "DocumentRequestById", id },
      ],
    }),
    companyUploadDocument: builder.mutation<IApiResponse<void>, FormData>({
      query: (data) => ({
        url: "/company/documents",
        method: "POST",
        body: data,
      }),
      invalidatesTags: (_result, _error, formData) => [
        "companyGetRecievedDocumentsRequests",
        "companyDocRequestStats",
        {
          type: "DocumentRequestById",
          id: formData.get("requestId") as string,
        },
      ],
    }),
    companyGetDocumentRequestById: builder.query<
      IApiResponse<IDocumentRequest>,
      string
    >({
      query: (requestId) => ({
        url: `/company/document-requests/${requestId}`,
      }),
      providesTags: (_result, _error, requestId) => [
        { type: "DocumentRequestById", id: requestId },
      ],
    }),
    approveDocumentRequest: builder.mutation<
      IApiResponse<void>,
      // `versionId`: the employee's pending version the reviewer looked at —
      // the API refuses (409) if a newer one was submitted meanwhile.
      { id: string; versionId?: number }
    >({
      query: ({ id, versionId }) => ({
        url: `/company/document-requests/${id}/approve`,
        method: "PUT",
        body: { versionId },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "companyGetSentDocumentsRequests",
        "companyGetRecievedDocumentsRequests",
        "companyDocRequestStats",
        { type: "DocumentRequestById", id },
        // The document itself moved to verified / rejected, and its history
        // records the decision — refetched on a 409 too, to show the newer
        // submission.
        "employeeDocuments",
        "companyGetDocuments",
        "DocumentVersions",
      ],
    }),
    rejectDocumentRequest: builder.mutation<
      IApiResponse<void>,
      { id: string; rejectionReason: string; versionId?: number }
    >({
      query: ({ id, rejectionReason, versionId }) => ({
        url: `/company/document-requests/${id}/reject`,
        method: "PUT",
        body: {
          rejectionReason,
          versionId,
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "companyGetSentDocumentsRequests",
        "companyGetRecievedDocumentsRequests",
        "companyDocRequestStats",
        { type: "DocumentRequestById", id },
        "employeeDocuments",
        "companyGetDocuments",
        "DocumentVersions",
      ],
    }),
  }),
});

export const {
  useCompanyGetDocumentsSummaryQuery,
  useCompanyGetEmployeeDocumentsQuery,
  useCompanyGetEmployeeDocumentsSummaryQuery,
  useCompanyGetEmployeeDocumentsByIdQuery,
  useCompanyGetPaginatedDocumentsQuery,
  useCompanyGetDocumentsInfiniteQuery,
  useCompanyGetDocumentByIdQuery,
  useCompanyGetDocumentVersionsQuery,
  useCompanyAddDocumentMutation,
  useCompanyUpdateDocumentMutation,
  useCompanyDeleteDocumentByIdMutation,
  useCompanyGetSentDocumentsRequestsQuery,
  useCreateDocumentRequestMutation,
  useCompanyUpdateDocumentRequestMutation,
  useCompanyCancelSentDocumentRequestMutation,
  useCompanyReopenSentDocumentRequestMutation,
  useCompanyGetRecievedDocumentsRequestsQuery,
  useGetCompanyDocumentRequestStatsQuery,
  useCompanyUploadDocumentMutation,
  useCompanyGetDocumentRequestByIdQuery,
  useApproveDocumentRequestMutation,
  useRejectDocumentRequestMutation,
} = companyDocumentsApi;
